import React, { useId, type ReactNode } from 'react';
import { DataTablePagination } from './Pagination';
import { cn } from '@/lib/utils';

export interface ColumnDef<TData, TKey extends keyof TData> {
	header: ReactNode;
	accessor: TKey;
	cell?: (value: TData[TKey], row: TData) => React.ReactNode;
	className?: string;
	headerClassName?: string;
	cellClassName?: string;
}

interface DataTableProps<TData> {
	columns: ColumnDef<TData, keyof TData>[];
	data: TData[];
	page?: number;
	limit?: number;
	totalItems?: number;
	onPageChange?: (page: number) => void;
	/** Ao informar, exibe campo para alterar quantos itens vêm por página (ex.: 15). Reseta página no pai. */
	onLimitChange?: (limit: number) => void;
	/** Limites do input "registros por página" (padrão 1–100). */
	pageSizeMin?: number;
	pageSizeMax?: number;
	className?: string;
	selectedItem?: string | number | null;
	/** Suporte opcional a múltiplas linhas selecionadas (ex.: seleção de pacientes). */
	selectedItems?: Array<string | number>;
	rowKey?: keyof TData;
	tableClassName?: string;
}

const DataTable = <TData extends object>({
	columns,
	data = [],
	page = 1,
	limit = 10,
	totalItems = 0,
	onPageChange,
	onLimitChange,
	pageSizeMin = 1,
	pageSizeMax = 100,
	className = '',
	selectedItem,
	selectedItems,
	rowKey = 'id' as keyof TData,
	tableClassName,
}: DataTableProps<TData>) => {
	const pageSizeInputId = useId();
	const firstRowIndex = (page - 1) * limit + 1;
	const lastRowIndex = Math.min(totalItems, page * limit);

	const handlePageSizeChange = (raw: string) => {
		if (!onLimitChange) return;
		const n = parseInt(raw, 10);
		if (Number.isNaN(n)) return;
		const clamped = Math.min(pageSizeMax, Math.max(pageSizeMin, n));
		if (clamped !== limit) onLimitChange(clamped);
	};

	return (
		<div
			className={cn(
				'flex w-full flex-col overflow-hidden rounded-2xl border border-solid border-stone-200 bg-white shadow-sm',
				className,
			)}
		>
			<div className="inline-block min-w-full overflow-x-auto align-middle">
				<table
					className={cn(
						'min-w-full border-separate border-spacing-0',
						tableClassName,
					)}
				>
					<thead className="bg-stone-50/50">
						<tr>
							{columns.map((column, idx) => (
								<th
									key={idx}
									scope="col"
									className={cn(
										'border-b border-solid border-stone-200 px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-stone-400',
										column.className,
										column.headerClassName,
									)}
								>
									{column.header}
								</th>
							))}
						</tr>
					</thead>
					<tbody className="divide-y divide-stone-100">
						{data && data.length > 0 ? (
							data.map((row, rowIdx) => {
								const rowKeyValue = (row as any)[rowKey] as
									| string
									| number
									| undefined;
								const isSelected = Array.isArray(selectedItems)
									? rowKeyValue != null &&
									  selectedItems.includes(
											rowKeyValue as string | number,
									  )
									: selectedItem != null &&
									  rowKeyValue ===
											(selectedItem as string | number);

								return (
									<tr
										key={rowIdx}
										className={cn(
											'group cursor-pointer transition-colors',
											isSelected
												? 'bg-zinc-50/90 hover:bg-zinc-50'
												: 'hover:bg-stone-50',
										)}
									>
										{columns.map((column, colIdx) => (
											<td
												key={colIdx}
												className={cn(
													'whitespace-nowrap px-6 py-4',
													colIdx === 0 && 'relative',
													column.className,
													column.cellClassName,
												)}
											>
												{colIdx === 0 && isSelected && (
													<div
														className="absolute left-0 top-1/2 z-10 h-6 w-1 -translate-y-1/2 rounded-r-full bg-zinc-700"
														aria-hidden
													/>
												)}
												{column.cell ? (
													column.cell(
														row[column.accessor],
														row,
													)
												) : (
													<span
														className={cn(
															'text-[13px] font-bold',
															isSelected
																? 'text-stone-900'
																: 'text-stone-600',
														)}
													>
														{String(
															row[
																column.accessor
															],
														)}
													</span>
												)}
											</td>
										))}
									</tr>
								);
							})
						) : (
							<tr>
								<td
									colSpan={columns.length}
									className="bg-stone-50/30 px-8 py-16 text-center text-[11px] font-bold uppercase italic tracking-widest text-stone-400"
								>
									Nenhum registro encontrado.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<div className="flex flex-col gap-3 border-t border-solid border-stone-200 bg-white px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:px-8">
				<div className="text-[9px] font-black uppercase tracking-widest text-stone-400">
					EXIBINDO{' '}
					<span className="text-stone-900">
						{totalItems > 0
							? `${firstRowIndex}-${lastRowIndex}`
							: 0}
					</span>{' '}
					DE <span className="text-stone-900">{totalItems}</span>{' '}
					REGISTROS
				</div>

				<div className="flex flex-wrap items-center gap-3 sm:justify-end sm:gap-4">
					{onLimitChange && (
						<div className="flex items-center gap-2">
							<label
								htmlFor={pageSizeInputId}
								className="whitespace-nowrap text-[9px] font-black uppercase tracking-widest text-stone-400"
							>
								Registros por página
							</label>
							<input
								id={pageSizeInputId}
								type="number"
								inputMode="numeric"
								min={pageSizeMin}
								max={pageSizeMax}
								value={limit}
								onChange={(e) =>
									handlePageSizeChange(e.target.value)
								}
								onKeyDown={(e) => {
									if (e.key === 'Enter') e.preventDefault();
								}}
								className="h-8 w-14 rounded-lg border border-solid border-stone-300 bg-white px-1 text-center text-[12px] font-bold text-stone-800 outline-none transition-colors focus:border-stone-500"
								aria-label="Quantidade de registros por página"
							/>
						</div>
					)}

					{onPageChange && (
						<DataTablePagination
							page={page}
							pageSize={limit}
							totalItems={totalItems}
							onPageChange={onPageChange}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export { DataTable };
